import {CalloutIcon, DotProgress, Pill} from '@wandb/components';
import {isMediaCardType} from '@wandb/weave/common/types/media';
import {ID} from '@wandb/weave/common/util/id';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import * as React from 'react';
import {useCallback} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  type AddPanelsAction,
  trackAddPanels,
} from '../../../services/analytics/addPanelsEvents';
import DOC_URLS, {lookupDocUrl} from '../../../util/doc_urls';
import {type LayedOutPanel} from '../../../util/panelTypes';
import {useOnboardingContext} from '../../../util/userContext';
import {panelViews} from '../../PanelBank/types';
import {useMediaKeys} from '../../PanelMediaBrowser/hooks/useMediaKeys';
import {getDefaultMediaKeysForType} from '../../PanelMediaBrowser/utils/getMediaKeys';
import {usePanelsQuickAddContext} from '../PanelsQuickAdd/PanelsQuickAddContext';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {
  DEFAULT_CONFIGS_BY_PANEL_TYPE,
  PANEL_VIEWS_BY_PANEL_TYPE,
} from './constants';
import {useIconColorContext} from './contexts/IconColorContext';
import {useNewPanelEditorContext} from './contexts/NewPanelEditorContext';
import type {AddPanelsMenuItemProps} from './types';

export const AddPanelsMenuItem = ({
  type,
  iconName,
  label,
  description,
  isDisabled = false,
  isLoading = false,
  newPillFeatureKey,
}: AddPanelsMenuItemProps) => {
  const {openWorkspaceDrawer, targetSectionRef} = useWorkspaceDrawerContext();
  const {openQuickAdd} = usePanelsQuickAddContext();
  const {setNewPanel} = useNewPanelEditorContext();
  const iconColor = useIconColorContext();
  const mediaKeysInfo = useMediaKeys();

  const isSelectionDisabled = isDisabled || isLoading;

  const trackAddPanelsBound = useCallback(
    (action: AddPanelsAction) => {
      trackAddPanels({
        action,
        scope: !targetSectionRef ? 'global' : 'section',
        panelType: type,
      });
    },
    [targetSectionRef, type]
  );

  const {setOnboardingState, onboardingState} =
    useOnboardingContext(newPillFeatureKey);

  const onSelect = useCallback(() => {
    if (isSelectionDisabled) {
      return;
    }

    trackAddPanelsBound('Panel type selected');

    if (type === 'quick-add') {
      openQuickAdd({
        location: 'Add panels drawer',
        sectionRef: targetSectionRef,
        onBack: () => openWorkspaceDrawer('add-panels', targetSectionRef),
      });
      return;
    }

    const isMedia = isMediaCardType(type);
    const viewType = isMedia
      ? panelViews.mediaBrowser
      : PANEL_VIEWS_BY_PANEL_TYPE[type];
    if (viewType) {
      const isWeaveTrace = viewType === panelViews.weaveTrace;
      const mediaKeys = isMedia
        ? getDefaultMediaKeysForType(mediaKeysInfo, type)
        : undefined;
      setNewPanel({
        __id__: ID(),
        // Provide a stable key so this manual panel matches the
        // auto-generated API-added spec with key 'weave-trace'.
        ...(isWeaveTrace ? {key: 'weave-trace'} : {}),
        viewType,
        config: mediaKeys
          ? {mediaKeys}
          : (DEFAULT_CONFIGS_BY_PANEL_TYPE[type] ?? {}),
        isAuto: false,
      } as LayedOutPanel);
    }
    if (newPillFeatureKey) {
      setOnboardingState({
        ...onboardingState,
        [newPillFeatureKey]: true,
      });
    }
  }, [
    isSelectionDisabled,
    mediaKeysInfo,
    openQuickAdd,
    openWorkspaceDrawer,
    setNewPanel,
    targetSectionRef,
    trackAddPanelsBound,
    type,
    newPillFeatureKey,
    onboardingState,
    setOnboardingState,
  ]);

  return (
    <div
      role="menuitem"
      data-test={`add-panel-${type}`}
      aria-disabled={isSelectionDisabled}
      className={twMerge(
        'flex items-center justify-between gap-12 rounded-lg px-16 py-8',
        !isSelectionDisabled && 'cursor-pointer hover:bg-wb-surface-hover',
        isSelectionDisabled && '[&:hover_a]:opacity-100'
      )}
      onClick={onSelect}>
      <div
        className={twMerge(
          'flex items-center gap-12',
          isSelectionDisabled && 'opacity-40'
        )}>
        {isLoading ? (
          <div className="flex items-center justify-center w-36 h-36">
            <DotProgress size="md" aria-label="Checking panel availability" />
          </div>
        ) : (
          <CalloutIcon color={iconColor} icon={iconName} size="sm" />
        )}
        <div>
          <p className="font-semibold">
            {newPillFeatureKey && !onboardingState[newPillFeatureKey] ? (
              <div className="flex items-center gap-4">
                <div>{label}</div>
                <span className="font-normal text-cactus-600">
                  <Pill color="cactus" textValue="New" />
                </span>
              </div>
            ) : (
              label
            )}
          </p>
          <p className="text-sm text-wb-text-tertiary">{description}</p>
        </div>
      </div>
      {isDisabled && (
        <TargetBlank
          href={lookupDocUrl(type) ?? DOC_URLS.logs}
          className="opacity-0 transition-opacity duration-100">
          <Button
            size="small"
            variant="secondary"
            endIcon="open-new-tab"
            onClick={() => trackAddPanelsBound('Disabled item docs clicked')}>
            Docs
          </Button>
        </TargetBlank>
      )}
    </div>
  );
};
